import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ErrorNotification, toaster } from '@mr-yum/frontend-ui'
import React, { useCallback } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useMutation } from 'urql'
import * as Yup from 'yup'

import { InputField } from '@/components/HookForm/InputField'
import { FloatingButton } from '@/components/Shared/FloatingButton'
import { CurrentUserQuery, UpdateCustomerDetailsDocument } from '@/gql/graphql'
import { useLoggerLegacy } from '@/hooks/useLogger'

const getValidationSchema = ({ formatMessage }: IntlShape) =>
  Yup.object()
    .noUnknown(false)
    .shape({
      name: Yup.string().required(
        formatMessage({
          defaultMessage: 'Your full name is required',
          id: '5xIJoS',
        }),
      ),
      email: Yup.string()
        .email(
          formatMessage({
            id: 'qDmele',
            defaultMessage: 'Email must be valid',
          }),
        )
        .required(
          formatMessage({
            id: 'gtleS5',
            defaultMessage: 'Please provide your email address',
          }),
        ),
      mobile: Yup.string().required(
        formatMessage({
          defaultMessage: 'Your mobile is required',
          id: 'nef+TJ',
        }),
      ),
    })

type FormValues = {
  name: string
  email: string
  mobile: string
}

interface Props {
  currentUser: CurrentUserQuery['currentUser']
  venueSlug: string
}

export const EditProfileForm = ({ currentUser, venueSlug }: Props) => {
  const { logEventLegacy } = useLoggerLegacy()
  const intl = useIntl()

  const [{ error }, updateUser] = useMutation(UpdateCustomerDetailsDocument)

  const defaultValues = {
    name: currentUser?.name ?? '',
    email: currentUser?.email ?? '',
    mobile: currentUser?.mobile ?? '',
  }

  const form = useForm<FormValues>({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(intl)),
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const submitHandler = useCallback<SubmitHandler<FormValues>>(
    async ({ name, email }) => {
      logEventLegacy('Updated profile details')

      const result = await updateUser({ name, email, venueSlug })

      if (result.error) {
        return
      }

      toaster.positive(
        <>
          <FormattedMessage
            defaultMessage="Successfully updated your account details"
            id="qLqvt7"
          />
        </>,
      )
    },
    [updateUser, venueSlug, logEventLegacy],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className="m-9">
          <div>
            {error && <ErrorNotification fullWidth error={error} />}

            <InputField
              autoFocus
              name="name"
              data-testid="name"
              label={intl.formatMessage({
                defaultMessage: 'Full name',
                id: 'yk4PT9',
              })}
            />

            <InputField
              name="email"
              data-testid="email"
              label={intl.formatMessage({
                defaultMessage: 'Email address',
                id: 'hJZwTS',
              })}
            />

            <InputField
              name="mobile"
              data-testid="mobile"
              label={intl.formatMessage({
                defaultMessage: 'Phone number',
                id: 'jdJhOL',
              })}
              readOnly
            />
          </div>
        </div>

        <FloatingButton>
          <Button isLoading={isSubmitting} type="submit" size="lg" fullWidth>
            <FormattedMessage defaultMessage="Save changes" id="X0ha1a" />
          </Button>
        </FloatingButton>
      </form>
    </FormProvider>
  )
}
